import { React, NavLink, useSelector, logo, Database, Box, useTheme, Divider } from '../../utils/globalImports';
import { useTranslation } from 'react-i18next';
import {
  Menu as CollapseMenuIcon,
  BrainCog,
  MessageSquare,
  FileCode,
  ChartNetwork,
  Share2,
} from 'lucide-react';

import { RootState } from '../../store';
import '../../assets/styles/VaultilakeSidebar.css';
import { ExtendedAccountResponse } from '../../store/types';

interface Props {
  visible: boolean;
  collapsed?: boolean;
  onCollapseToggle?: () => void;
}

const VaultilakeSidebar: React.FC<Props> = ({ visible, collapsed = false, onCollapseToggle }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const auth = useSelector((state: RootState) => state.auth);
  const selectedOntologyId = useSelector((state: RootState) => state.vaultilake.selectedOntologyId);
  const account = auth.account as ExtendedAccountResponse | null;
  const userRole = (account?.user?.role || account?.role)?.trim().toLowerCase();

  const hasOntologyAccess = userRole && ['superadmin', 'customeradmin'].includes(userRole);
  const hasDataConnectionAccess = userRole && ['superadmin', 'customeradmin', 'clientadmin'].includes(userRole);
  const ontologyPath = selectedOntologyId ? `/ontology/catalogue/${selectedOntologyId}` : '/ontology/catalogue';

  return (
    <div
      className={`customer-sidebar vaultilake-sidebar ${
        visible ? 'slide-in' : 'slide-out'
      }${collapsed ? ' collapsed' : ''}`}
    >
      {/* Logo + collapse toggle */}
      <div className="sidebar-logo-container">
        {!collapsed && <img src={logo} alt="Logo" className="sidebar-logo" />}
        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={onCollapseToggle}
          title={collapsed ? t('vaultilakeChat.sidebar.expand') : t('vaultilakeChat.sidebar.collapse')}
        >
          <CollapseMenuIcon size={20} />
        </button>
      </div>

      {/* Navigation items */}
      <div className="sidebar-item">
        <div className="sidebar-link-with-toggle">
          <NavLink to="/vaultilake-chat" className={({ isActive }) => (isActive ? 'sub-active-link' : '')}>
            <div className="navlink-content">
              <MessageSquare className="sub-sidebar-icon" />
              {!collapsed && <span>{t('vaultilakeChat.sidebar.aiChat')}</span>}
            </div>
          </NavLink>
        </div>
      </div>

      <div className="sidebar-item">
        <div className="sidebar-link-with-toggle">
          <NavLink to="/data-platform" className={({ isActive }) => (isActive ? 'sub-active-link' : '')}>
            <div className="navlink-content">
              <Database className="sub-sidebar-icon" />
              {!collapsed && <span>{t('vaultilake.subMenuItems.dataPlatform')}</span>}
            </div>
          </NavLink>
        </div>
      </div>

      <div className="sidebar-item">
        <div className="sidebar-link-with-toggle">
          <NavLink to="lakehouses/sql-queries" className={({ isActive }) => (isActive ? 'sub-active-link' : '')}>
            <div className="navlink-content">
              <FileCode className="sub-sidebar-icon" />
              {!collapsed && <span>{t('vaultilake.subMenuItems.sqlQueries')}</span>}
            </div>
          </NavLink>
        </div>
      </div>

      <div className="sidebar-item">
        <div className="sidebar-link-with-toggle">
          <NavLink
            to="/data-agents/data-agent-management"
            className={({ isActive }) => (isActive ? 'sub-active-link' : '')}
          >
            <div className="navlink-content">
              <BrainCog className="sub-sidebar-icon" />
              {!collapsed && <span>{t('menuItems.dataAgents')}</span>}
            </div>
          </NavLink>
        </div>
      </div>

      {/* <div className="sidebar-item">
        <div className="sidebar-link-with-toggle">
          <NavLink to="/knowledge-agent-management" className={({ isActive }) => (isActive ? 'sub-active-link' : '')}>
            <div className="navlink-content">
              <BookOpen className="sub-sidebar-icon" />
              {!collapsed && <span>KG Studio</span>}
            </div>
          </NavLink>
        </div>
      </div> */}

      {hasOntologyAccess && (
        <div className="sidebar-item">
          <div className="sidebar-link-with-toggle">
            <NavLink to={ontologyPath} className={({ isActive }) => (isActive ? 'sub-active-link' : '')}>
              <div className="navlink-content">
                <Share2 className="sub-sidebar-icon" />
                {!collapsed && <span>Graph (Ontology)</span>}
              </div>
            </NavLink>
          </div>
        </div>
      )}

      {hasDataConnectionAccess && (
        <div className="sidebar-item">
          <div className="sidebar-link-with-toggle">
            <NavLink
              to="/lakehouses/data-connection-management"
              className={({ isActive }) => (isActive ? 'sub-active-link' : '')}
            >
              <div className="navlink-content">
                <ChartNetwork className="sub-sidebar-icon" />
                {!collapsed && <span>{t('connectors.dataConnectors')}</span>}
              </div>
            </NavLink>
          </div>
        </div>
      )}

      {/* Spacer so the sidebar doesn't look cramped */}
      {!collapsed && <Box sx={{ flex: 1 }} />}
    </div>
  );
};

export default VaultilakeSidebar;
